import React, { useEffect } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Input, Button, Checkbox, Spin } from "antd";
import {
  useCheckPanelUserSurveyAccessQuery,
  useGetFormDetailsQuery,
  useGetFormQuestionsQuery,
} from "../../store/api/apiSlice";
import CustomVideoPlayer from "../../components/common/CustomVideoPlayer";
import { SurveyHeader } from "../../components/survey/SurveyHeader";
import {
  getPanelSurveyAccessBlock,
  getSurveyTrackingToken,
  persistSurveyTrackingToken,
  withSurveyQueryString,
} from "../../utils/surveyTracking";

const SurveyIntro = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const withQuery = (path) => withSurveyQueryString(path, searchParams);
  const trackingToken = getSurveyTrackingToken(formId, searchParams);
  const [form] = Form.useForm();
  const { data: formData, isLoading: formLoading } =
    useGetFormDetailsQuery(formId);
  const { data: questionsData, isLoading: questionsLoading } =
    useGetFormQuestionsQuery(formId, {
      skip: !formId,
    });
  const { data: surveyAccessData, isLoading: accessLoading } =
    useCheckPanelUserSurveyAccessQuery(trackingToken ?? skipToken);

  const introQuestion = questionsData?.data?.find((q) => q.is_intro);
  const organization = formData?.data?.organization;
  const organizationTermsRoute = `/organization-terms-and-conditions?formId=${encodeURIComponent(
    formId,
  )}`;
  const organizationPrivacyRoute = `/organization-privacy-policy?formId=${encodeURIComponent(
    formId,
  )}`;

  useEffect(() => {
    persistSurveyTrackingToken(formId, searchParams);
  }, [formId, searchParams]);

  useEffect(() => {
    if (!trackingToken || accessLoading || !surveyAccessData) return;

    const accessBlock = getPanelSurveyAccessBlock(surveyAccessData);

    if (!accessBlock) return;

    navigate(withQuery(`/form-submissions/panel-error-page/${formId}`), {
      replace: true,
      state: {
        reason: accessBlock,
        canRetryPayout: surveyAccessData?.can_retry_payout === true,
        payout: surveyAccessData?.payout || {
          canRetry: surveyAccessData?.can_retry_payout === true,
          payoutSent: false,
          payoutError: surveyAccessData?.payout_error || null,
        },
      },
    });
  }, [
    trackingToken,
    accessLoading,
    surveyAccessData,
    formId,
    navigate,
    searchParams,
  ]);

  const handleSubmit = (values) => {
    if (introQuestion) {
      const responses = {
        [introQuestion._id]: values,
      };
      localStorage.setItem(
        `survey_${formId}_responses`,
        JSON.stringify(responses),
      );
    }

    localStorage.setItem(
      "surveyUserData",
      JSON.stringify({
        fullName: values.fullName,
        email: values.email,
        anonymous: values.anonymous,
      }),
    );

    const firstQuestion = questionsData.data.find((q) => !q.is_intro);

    if (firstQuestion) {
      navigate(
        withQuery(
          `/form-submissions/question/${formId}/${firstQuestion.number}`,
        ),
      );
    } else {
      navigate(withQuery("/form-submissions/thank-you"));
    }
  };

  if (formLoading || questionsLoading || (trackingToken && accessLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-3 md:p-4">
      <div className="w-full mx-auto">
        <SurveyHeader
          organization={formData?.data?.organization}
          form={formData?.data}
          onCancel={() => navigate("/")}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 border rounded-2xl p-4 sm:p-6 md:p-8">
          <div className="space-y-4 sm:space-y-6 order-3 md:order-2">
            <div className="order-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">
                {formData?.data?.title}
              </h1>
              <p className="text-gray-400 text-sm sm:text-base">
                {formData?.data?.description}
              </p>
            </div>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                terms: false,
              }}
              className="space-y-4 order-3"
            >
              <div className="grid grid-cols-1 gap-3">
                <Form.Item
                  label="Full Name"
                  name="fullName"
                  className="mb-2 sm:mb-3"
                  rules={[{ required: !formData?.data?.anonymous }]}
                >
                  <Input placeholder="Please enter your full name..." />
                </Form.Item>
                <Form.Item
                  label="Email Address"
                  name="email"
                  className="mb-2 sm:mb-3"
                  rules={[{ required: !formData?.data?.anonymous }]}
                >
                  <Input placeholder="Please enter your email address..." />
                </Form.Item>
              </div>
              {introQuestion?.response?.map((input, index) => (
                <Form.Item
                  key={input._id}
                  label={input.input_title}
                  name={["responses", index.toString()]}
                  rules={[
                    {
                      required: true,
                      message: `Please enter ${input.input_title}`,
                    },
                  ]}
                >
                  <DynamicInput input={input} />
                </Form.Item>
              ))}
              {formData?.data?.anonymous && (
                <Form.Item name="anonymous" valuePropName="checked">
                  <Checkbox>Make My Response Anonymous</Checkbox>
                </Form.Item>
              )}
              <Form.Item
                name="terms"
                valuePropName="checked"
                rules={[{ required: true, message: "Please accept the terms" }]}
              >
                <Checkbox>
                  Accept our{" "}
                  <a
                    href={organizationTermsRoute}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline cursor-pointer"
                  >
                    Terms & Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href={organizationPrivacyRoute}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline cursor-pointer"
                  >
                    Privacy Policy
                  </a>
                </Checkbox>
              </Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="rounded-2xl px-6 sm:px-8 md:px-10 h-10 sm:h-11 w-full"
              >
                Start Survey
              </Button>
            </Form>
          </div>
          <div className="mx-auto w-full order-2 md:order-1">
            {introQuestion?.video ? (
              <CustomVideoPlayer
                controls
                preload="metadata"
                src={introQuestion.video}
                playsInline
                className="w-full h-auto max-h-[400px] sm:max-h-[450px] md:max-h-[86vh] object-cover rounded-lg"
              />
            ) : (
              organization?.logo && (
                <img
                  src={organization.logo}
                  alt={organization.name}
                  className="w-full h-auto max-h-[400px] sm:max-h-[450px] md:max-h-[86vh] object-cover rounded-lg"
                />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default SurveyIntro;
